import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import patientService from '../../../services/patient.service';
import './EcxHeader.css';

const ROUTE_TABS = [
    { match: '/appointments', name: 'Appointments', icon: 'bi-calendar-event' },
    { match: '/web-bookings', name: 'Web Bookings', icon: 'bi-calendar-check' },
    { match: '/patient-files', name: 'Patient Files', icon: 'bi-folder2-open' },
    { match: '/messages', name: 'Messages', icon: 'bi-chat-left-text' },
    { match: '/surveys', name: 'Surveys', icon: 'bi-clipboard-check' },
    { match: '/articles', name: 'Articles', icon: 'bi-file-text' },
    { match: '/newsletter', name: 'Newsletter', icon: 'bi-envelope' },
    { match: '/blast', name: 'Blast', icon: 'bi-megaphone' },
    { match: '/reputation', name: 'Reputation', icon: 'bi-star' },
    { match: '/my-files', name: 'My Files', icon: 'bi-folder' },
    { match: '/eye-draw', name: 'Eye Draw', icon: 'bi-easel' },
    { match: '/activity', name: 'Activity', icon: 'bi-clock-history' },
    { match: '/invalid-details', name: 'Invalid Details', icon: 'bi-exclamation-octagon' },
    { match: '/settings', name: 'Settings', icon: 'bi-gear' },
];

const HEADER_LINKS = [
    { to: '/invalid-details', name: 'Invalid Details', icon: 'bi-exclamation-octagon' },
    { to: '/eye-draw', name: 'Eye Draw', icon: 'bi-easel' },
];

const getCurrentTab = (pathname) => {
    const match = ROUTE_TABS.find((tab) => pathname.startsWith(tab.match));
    return match ? { ...match, color: 'text-primary' } : null;
};

const formatInvalidDetailsBadgeCount = (count) => {
    if (count > 99) {
        return '99+';
    }

    return String(count);
};

const getUserDisplayName = (userInfo) => {
    const name = [userInfo?.firstName, userInfo?.lastName].filter(Boolean).join(' ').trim();

    return name || null;
};

const getUserInitials = (userInfo) => {
    const first = userInfo?.firstName?.[0] || '';
    const last = userInfo?.lastName?.[0] || '';
    const initials = `${first}${last}`.trim().toUpperCase();

    return initials || null;
};

function EcxHeader() {
    const location = useLocation();
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);
    const userInfo = useEyeconxStore((state) => state.userInfo);
    const invalidDetailsCount = useEyeconxStore((state) => state.invalidDetailsCount);
    const setInvalidDetailsCount = useEyeconxStore((state) => state.setInvalidDetailsCount);
    const sidebarBroken = useEyeconxStore((state) => state.sidebarBroken);
    const toggleSidebar = useEyeconxStore((state) => state.toggleSidebar);
    const userPanelOpen = useEyeconxStore((state) => state.userPanelOpen);
    const setUserPanelOpen = useEyeconxStore((state) => state.setUserPanelOpen);

    const [menuOpen, setMenuOpen] = useState(false);
    const currentTab = getCurrentTab(location.pathname);
    const userDisplayName = getUserDisplayName(userInfo);
    const userInitials = getUserInitials(userInfo);

    const fetchInvalidDetailsCount = useCallback(async () => {
        if (!clinicId) {
            return;
        }

        try {
            const res = await patientService.getInvalidDetailsList(clinicId, 0, 1);
            setInvalidDetailsCount(res.data?.totalElements ?? 0);
        } catch (err) {
            console.error('Failed to load invalid details count', err);
            setInvalidDetailsCount(null);
        }
    }, [clinicId, setInvalidDetailsCount]);

    useEffect(() => {
        fetchInvalidDetailsCount();
    }, [fetchInvalidDetailsCount, location.pathname]);

    const toggleMenu = () => setMenuOpen((open) => !open);

    const navLinkClass = ({ isActive }) =>
        `nav-link ecx-header-nav-link px-3 py-2 rounded-3 mx-1${
            isActive ? ' ecx-header-nav-link--active' : ''
        }`;

    const mobileNavLinkClass = ({ isActive }) =>
        `nav-link ecx-header-nav-link ecx-header-nav-link--mobile px-3 py-3 rounded-3${
            isActive ? ' ecx-header-nav-link--active' : ''
        }`;

    const renderHeaderLink = (link, { mobile = false, onNavigate } = {}) => {
        const linkClass = mobile ? mobileNavLinkClass : navLinkClass;
        const showInvalidDetailsBadge =
            link.to === '/invalid-details' && invalidDetailsCount !== null && invalidDetailsCount > 0;

        return (
            <NavLink
                to={link.to}
                className={linkClass}
                onClick={onNavigate}
            >
                <i className={`bi ${link.icon} me-2`}></i>
                {link.name}
                {showInvalidDetailsBadge && (
                    <sup
                        className="ecx-header-link-badge"
                        aria-label={`${invalidDetailsCount} invalid detail records`}
                    >
                        {formatInvalidDetailsBadgeCount(invalidDetailsCount)}
                    </sup>
                )}
            </NavLink>
        );
    };

    return (
        <nav className="navbar navbar-expand-lg navbar-light bg-white sticky-top shadow-sm px-1 p-0 ecx-header">
            <div className="container-fluid p-3">
                <div className="d-flex align-items-center min-width-0 flex-grow-1">
                    {sidebarBroken && (
                        <button
                            type="button"
                            className="btn btn-light border-0 me-2 d-md-none flex-shrink-0"
                            onClick={toggleSidebar}
                            aria-label="Toggle navigation menu"
                        >
                            <i className="bi bi-list fs-4"></i>
                        </button>
                    )}

                    <span className="navbar-brand mb-0 h5 fw-bold text-dark me-3 flex-shrink-0">
                        <i className="bi bi-eye me-2 text-primary"></i>
                        EyeconX
                    </span>

                    {currentTab && (
                        <div className={`d-none d-md-flex align-items-center min-width-0 ${currentTab.color}`}>
                            <div className="vr mx-2 opacity-50 flex-shrink-0" style={{ height: '24px' }}></div>
                            <i className={`bi ${currentTab.icon} me-2 flex-shrink-0`}></i>
                            <span className="fw-medium text-truncate">{currentTab.name}</span>
                        </div>
                    )}
                </div>

                <div className={`collapse navbar-collapse ${menuOpen ? 'show' : ''}`}>
                    <ul className="navbar-nav ms-auto align-items-lg-center">
                        {HEADER_LINKS.map((link) => (
                            <li key={link.to} className="nav-item d-none d-lg-block">
                                {renderHeaderLink(link)}
                            </li>
                        ))}

                        {currentTab && (
                            <li className="nav-item d-block d-lg-none mt-2">
                                <div className="nav-link px-3 py-2 rounded-3 bg-primary bg-opacity-10 text-primary fw-medium">
                                    <i className={`bi ${currentTab.icon} me-2`}></i>
                                    {currentTab.name}
                                </div>
                            </li>
                        )}

                        {HEADER_LINKS.map((link) => (
                            <li key={`mobile-${link.to}`} className="nav-item d-block d-lg-none">
                                {renderHeaderLink(link, { mobile: true, onNavigate: toggleMenu })}
                            </li>
                        ))}
                    </ul>
                </div>

                <button
                    type="button"
                    className={`btn border-0 ecx-header-icon-btn ecx-header-user-btn ms-2 flex-shrink-0 ${
                        userPanelOpen ? 'ecx-header-user-btn--active' : ''
                    }`}
                    onClick={() => setUserPanelOpen(!userPanelOpen)}
                    aria-label="Open account menu"
                    aria-expanded={userPanelOpen}
                >
                    <span className="ecx-header-user-btn__content">
                        <span className="ecx-header-user-icon" aria-hidden="true">
                            {userInitials || <i className="bi bi-person-fill" />}
                        </span>
                        {userDisplayName && (
                            <span className="ecx-header-user-name">{userDisplayName}</span>
                        )}
                    </span>
                </button>

                <button
                    className="navbar-toggler border-0 ecx-header-toggler ms-2 flex-shrink-0"
                    type="button"
                    onClick={toggleMenu}
                    aria-expanded={menuOpen}
                    aria-label="Toggle navigation"
                >
                    <i className={`bi ${menuOpen ? 'bi-x-lg' : 'bi-list'}`}></i>
                </button>
            </div>
        </nav>
    );
}

export default EcxHeader;
